import { ReactElement, useMemo } from 'react'
import { commentPlanFilterLabels } from '@constants'
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
} from '@mui/material'
import { storeyToLabel } from '@editor/pages/Plans/components/AddPlanDialog/components/utils'
import { roofStoreyKey } from '@editorStores'
import { PLAN_VIEW_PLAN_CHOICES, PlanViewPlanChoice, PlanViewPlanType } from '../constants'
import { sortStoreysForSelect } from '../utils'

interface Props {
  storeys: readonly string[]
  selectedStoreys: readonly string[]
  /** Plan the scene previews (the last previewable choice while "Lastplan" is chosen). */
  planType: PlanViewPlanType
  planChoice: PlanViewPlanChoice
  onStoreysChange: (storeys: string[]) => void
  onPlanChoiceChange: (planChoice: PlanViewPlanChoice) => void
}

/**
 * Drawer section choosing the plan (Positionsplan / Aussteifungsplan / Lastplan) and the storey to
 * preview. The load plan always covers the whole building, so it has no storey selection.
 * With "Dach" on the position plan, further storeys can be added to the same plan (like
 * "Dach + weitere" on the plans page); the stiffening plan never contains the roof.
 */
const PlanViewSettingsSection = ({
  storeys,
  selectedStoreys,
  planType,
  planChoice,
  onStoreysChange,
  onPlanChoiceChange,
}: Props): ReactElement => {
  const orderedStoreys = useMemo(() => sortStoreysForSelect(storeys), [storeys])
  const regularStoreys = useMemo(
    () => orderedStoreys.filter(storey => storey !== roofStoreyKey),
    [orderedStoreys],
  )

  const isRoofSelected = selectedStoreys.includes(roofStoreyKey)
  const mainStorey = isRoofSelected ? roofStoreyKey : selectedStoreys[0] ?? ''
  const additionalStoreys = selectedStoreys.filter(storey => storey !== roofStoreyKey)
  const showAdditionalStoreys = planType === 'positionPlan' && isRoofSelected

  const onAdditionalStoreysChange = (value: string | string[]) => {
    const next = typeof value === 'string' ? value.split(',').filter(Boolean) : value
    onStoreysChange([roofStoreyKey, ...next])
  }

  return (
    <Stack spacing={2} pt={1}>
      <FormControl component="fieldset">
        <FormLabel component="legend" sx={{ fontSize: '0.75rem' }}>
          Plantyp
        </FormLabel>
        <RadioGroup
          value={planChoice}
          onChange={event => onPlanChoiceChange(event.target.value as PlanViewPlanChoice)}
          data-cy="plan-view-plan-type-radio-group"
        >
          {PLAN_VIEW_PLAN_CHOICES.map(availablePlanType => (
            <FormControlLabel
              key={availablePlanType}
              value={availablePlanType}
              control={<Radio size="small" />}
              label={commentPlanFilterLabels[availablePlanType]}
              data-cy={`plan-view-plan-type-${availablePlanType}`}
            />
          ))}
        </RadioGroup>
      </FormControl>

      {planChoice !== 'loadPlan' && (
        <>
          <TextField
            select
            size="small"
            label="Geschoss"
            value={mainStorey}
            onChange={event => onStoreysChange([event.target.value])}
            data-cy="plan-view-storey-select"
          >
            {orderedStoreys.map(availableStorey => (
              <MenuItem
                key={availableStorey}
                value={availableStorey}
                // The stiffening plan never contains the roof.
                disabled={planType === 'stiffeningPlan' && availableStorey === roofStoreyKey}
                data-cy={`plan-view-storey-${availableStorey}`}
              >
                {storeyToLabel(availableStorey)}
              </MenuItem>
            ))}
          </TextField>

          {showAdditionalStoreys && (
            <TextField
              select
              size="small"
              label="Weitere Geschosse"
              value={additionalStoreys}
              onChange={event => onAdditionalStoreysChange(event.target.value)}
              SelectProps={{
                multiple: true,
                renderValue: value => (value as string[]).map(storeyToLabel).join(', '),
              }}
              data-cy="plan-view-additional-storeys-select"
            >
              {regularStoreys.map(availableStorey => (
                <MenuItem
                  key={availableStorey}
                  value={availableStorey}
                  data-cy={`plan-view-additional-storey-${availableStorey}`}
                >
                  <Checkbox size="small" checked={additionalStoreys.includes(availableStorey)} />
                  {storeyToLabel(availableStorey)}
                </MenuItem>
              ))}
            </TextField>
          )}
        </>
      )}
    </Stack>
  )
}

export default PlanViewSettingsSection
